import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { segments as segmentsApi, accessTypes as atApi, agreementFiles as filesApi, users as usersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { categoryLabel, EXPANSION_STATUSES, expansionStatusLabel } from '../utils/categories';
import { footingColor, footingDescription, FOOTING_SCORES } from '../utils/footing';
import { segmentName } from '../utils/segments';

// Trail owner picker. Keeps a current owner who's no longer an active
// volunteer visible (and flagged) instead of silently showing "no owner".
function OwnerSelect({ value, fallbackName, roster, onChange, disabled, style }) {
  const known = !value || roster.some(v => v.id === value);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value || null)} disabled={disabled} style={style}>
      <option value="">— No owner —</option>
      {!known && <option value={value}>{fallbackName || 'Unknown'} (not an active volunteer)</option>}
      {roster.map(v => <option key={v.id} value={v.id}>{v.display_name}</option>)}
    </select>
  );
}

export default function SegmentsPage() {
  const { user, isRealAdmin, isChair } = useAuth();
  const canDeleteSeg = isRealAdmin || isChair;
  // Admins and chairs set trail owners.
  const canAssign = isRealAdmin || isChair;
  const [roster, setRoster] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState(''); // '' all | 'none' | user id
  const [bulk, setBulk] = useState({ trail: '', owner: '', onlyUnassigned: true });
  const [assigning, setAssigning] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [allSegments, setAllSegments] = useState([]);
  const [allAccessTypes, setAllAccessTypes] = useState([]);
  const [editingSeg, setEditingSeg] = useState(null);
  const [autoEditId, setAutoEditId] = useState(() => searchParams.get('edit'));
  const [segFiles, setSegFiles] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [fileSearch, setFileSearch] = useState('');
  const [segFilter, setSegFilter] = useState('');
  const [message, setMessage] = useState(null);
  const [segComments, setSegComments] = useState([]);
  const [newSegComment, setNewSegComment] = useState('');
  const [postingSegComment, setPostingSegComment] = useState(false);

  const fmtCommentDate = (d) =>
    new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const loadSegments = useCallback(async () => { setAllSegments(await segmentsApi.list()); }, []);
  const loadAccessTypes = useCallback(async () => { setAllAccessTypes(await atApi.list()); }, []);

  useEffect(() => { loadSegments(); loadAccessTypes(); }, [loadSegments, loadAccessTypes]);

  useEffect(() => {
    if (!canAssign) return;
    usersApi.listVolunteers().then(setRoster)
      .catch(err => setMessage(`Error: couldn't load the volunteer list — ${err.message}`));
  }, [canAssign]);

  // Set the owner of one or more segments, then refresh the table.
  const assignOwners = async (ids, ownerId) => {
    if (!ids.length) return;
    setAssigning(true);
    try {
      const r = await segmentsApi.assignOwners(ids, ownerId);
      const name = ownerId ? (roster.find(v => v.id === ownerId) || {}).display_name : null;
      setMessage(ids.length === 1
        ? (name ? `Trail owner set to ${name}` : 'Trail owner cleared')
        : `${r.updated} segments ${name ? `assigned to ${name}` : 'cleared'}`);
      await loadSegments();
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setAssigning(false); }
  };

  // Auto-open edit modal if ?edit=segmentId is in URL
  useEffect(() => {
    if (autoEditId && allSegments.length > 0 && !editingSeg) {
      const seg = allSegments.find(s => s.id === autoEditId);
      if (seg) {
        startEditSeg(seg);
        setAutoEditId(null);
        setSearchParams({}, { replace: true });
      }
    }
  }, [autoEditId, allSegments]); // eslint-disable-line

  const handleKmlUpload = async (segmentId, file) => {
    try { await segmentsApi.uploadKml(segmentId, file); setMessage('KML uploaded'); loadSegments(); }
    catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const loadSegFiles = async (segmentId) => {
    try { setSegFiles(await filesApi.list(segmentId)); } catch { setSegFiles([]); }
  };

  const loadSegComments = async (segmentId) => {
    try { setSegComments(await segmentsApi.committeeComments(segmentId)); } catch { setSegComments([]); }
  };

  const handleAddSegComment = async () => {
    const body = newSegComment.trim();
    if (!body || !editingSeg) return;
    setPostingSegComment(true);
    try {
      await segmentsApi.addCommitteeComment(editingSeg.id, body);
      setNewSegComment('');
      loadSegComments(editingSeg.id);
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setPostingSegComment(false); }
  };

  const handleDeleteSegComment = async (id) => {
    if (!window.confirm('Delete this committee comment?')) return;
    try {
      await segmentsApi.deleteCommitteeComment(id);
      if (editingSeg) loadSegComments(editingSeg.id);
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const startEditSeg = async (seg) => {
    setEditingSeg({ ...seg });
    setNewSegComment('');
    loadSegFiles(seg.id);
    loadSegComments(seg.id);
    try { setAllFiles(await filesApi.listAll()); } catch { setAllFiles([]); }
  };

  const handleLinkFile = async (fileId) => {
    if (!editingSeg) return;
    try {
      await filesApi.link(fileId, editingSeg.id);
      setMessage('Document linked');
      loadSegFiles(editingSeg.id);
      setFileSearch('');
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Remove this document link?')) return;
    try {
      await filesApi.delete(fileId);
      setMessage('Document removed');
      if (editingSeg) loadSegFiles(editingSeg.id);
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleDeleteSeg = async () => {
    if (!editingSeg) return;
    if (!window.confirm(`Permanently delete "${segmentName(editingSeg)}"? This cannot be undone.`)) return;
    try {
      await segmentsApi.delete(editingSeg.id);
      setMessage('Segment deleted');
      setEditingSeg(null);
      setSegFiles([]);
      loadSegments();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  return (
    <div className="admin-page">
      <h1>Segments</h1>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {/* Edit modal */}
      {editingSeg && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setEditingSeg(null); setSegFiles([]); } }}>
        <div className="modal-content segment-edit-form">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <h3 style={{margin: 0}}>Edit: {segmentName(editingSeg)}</h3>
            <button className="btn-close" onClick={() => { setEditingSeg(null); setSegFiles([]); }}>✕</button>
          </div>
          <div className="edit-grid">
            <label>Trail Name
              <select value={editingSeg.trail_name || ''} onChange={e => {
                const newName = e.target.value;
                if (newName === editingSeg.trail_name) return;
                // Moving to a different trail: assign the next free segment number
                // so we don't collide with an existing (trail_name, segment_number).
                const used = allSegments
                  .filter(s => s.trail_name === newName && s.id !== editingSeg.id)
                  .map(s => s.segment_number || 0);
                const nextNum = (used.length ? Math.max(...used) : 0) + 1;
                setEditingSeg({...editingSeg, trail_name: newName, segment_number: nextNum});
              }}>
                {[...new Set(allSegments.map(s => s.trail_name))].sort().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label>Segment # <input type="number" value={editingSeg.segment_number || ''} onChange={e => setEditingSeg({...editingSeg, segment_number: parseInt(e.target.value) || ''})} /></label>
            <label>Segment Name <input type="text" value={editingSeg.segment_label || ''} placeholder={`${editingSeg.trail_name} #${editingSeg.segment_number}`}
              onChange={e => setEditingSeg({...editingSeg, segment_label: e.target.value})} /></label>
            {canAssign && (
              <label>Trail Owner
                <OwnerSelect value={editingSeg.assigned_volunteer_id} fallbackName={editingSeg.assigned_volunteer_name}
                  roster={roster} onChange={v => setEditingSeg({...editingSeg, assigned_volunteer_id: v})} />
              </label>
            )}
            <label>Category
              <select value={editingSeg.category || 'internal'} onChange={e => {
                const category = e.target.value;
                setEditingSeg({
                  ...editingSeg, category,
                  expansion_status: category === 'expansion' ? (editingSeg.expansion_status || 'identified') : editingSeg.expansion_status,
                });
              }}>
                <option value="internal">Internal</option>
                <option value="external">Roadside</option>
                <option value="courtesy">Courtesy</option>
                <option value="expansion">Expansion (proposed)</option>
              </select>
            </label>
            {editingSeg.category === 'expansion' && (
              <label>Expansion Status
                <select value={editingSeg.expansion_status || 'identified'} onChange={e => setEditingSeg({...editingSeg, expansion_status: e.target.value})}>
                  {EXPANSION_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            )}
            <label>Description <textarea value={editingSeg.description || ''} onChange={e => setEditingSeg({...editingSeg, description: e.target.value})} rows={2} /></label>
            <label>Access Type
              <select value={editingSeg.access_type_id || ''} onChange={e => setEditingSeg({...editingSeg, access_type_id: e.target.value || null})}>
                <option value="">— None —</option>
                {allAccessTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
              </select>
            </label>
            <label>Agreement Ref <input type="text" value={editingSeg.agreement_ref || ''} onChange={e => setEditingSeg({...editingSeg, agreement_ref: e.target.value})} /></label>
            <label>APN <input type="text" placeholder="xxx-xxx-xxx" value={editingSeg.apn || ''} onChange={e => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
              let formatted = digits;
              if (digits.length > 6) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
              else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
              setEditingSeg({...editingSeg, apn: formatted});
            }} /></label>
            <label>Address <input type="text" value={editingSeg.address || ''} onChange={e => setEditingSeg({...editingSeg, address: e.target.value})} /></label>
            <label>Landowner <input type="text" value={editingSeg.landowner_name || ''} onChange={e => setEditingSeg({...editingSeg, landowner_name: e.target.value})} /></label>
            <label>Contact Email <input type="email" value={editingSeg.contact_email || ''} onChange={e => setEditingSeg({...editingSeg, contact_email: e.target.value})} /></label>
            <label>Contact Phone <input type="tel" value={editingSeg.contact_phone || ''} onChange={e => setEditingSeg({...editingSeg, contact_phone: e.target.value})} /></label>
            <label>Parcel Sale Date <input type="date" value={editingSeg.parcel_sale_date || ''} onChange={e => setEditingSeg({...editingSeg, parcel_sale_date: e.target.value || null})} /></label>
            <label>Agreement Date <input type="date" value={editingSeg.agreement_date || ''} onChange={e => setEditingSeg({...editingSeg, agreement_date: e.target.value || null})} /></label>
            <div style={{gridColumn: '1 / -1'}}>
              <div style={{fontWeight: 500, marginBottom: '0.25rem'}}>Committee Comments</div>
              {segComments.length === 0 && (
                <p style={{color: '#6c757d', fontSize: '0.85rem', margin: '0 0 0.5rem'}}>No comments yet.</p>
              )}
              {segComments.map(c => (
                <div key={c.id} style={{marginBottom: '0.5rem'}}>
                  <div style={{whiteSpace: 'pre-wrap'}}>{c.body}</div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <em style={{fontSize: '0.8rem', color: '#6c757d'}}>
                      — {c.author_name || 'Unknown'}, {fmtCommentDate(c.created_at)}
                    </em>
                    {(isRealAdmin || isChair || c.author_id === user?.id) && (
                      <button type="button" onClick={() => handleDeleteSegComment(c.id)} title="Delete comment"
                        style={{border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem', padding: 0}}>✕</button>
                    )}
                  </div>
                </div>
              ))}
              <textarea value={newSegComment} onChange={e => setNewSegComment(e.target.value)}
                placeholder="Add a committee comment..." rows={2} style={{width: '100%'}} />
              <button type="button" className="btn-sm btn-primary" style={{marginTop: '0.35rem'}}
                onClick={handleAddSegComment} disabled={postingSegComment || !newSegComment.trim()}>
                {postingSegComment ? 'Adding...' : 'Add comment'}
              </button>
            </div>
            <label>Length (ft) <input type="number" step="1" value={editingSeg.length_feet || ''} onChange={e => setEditingSeg({...editingSeg, length_feet: e.target.value})} /></label>
            <label>Surface <select value={editingSeg.surface_type || ''} onChange={e => setEditingSeg({...editingSeg, surface_type: e.target.value || null})}>
              <option value="">—</option><option value="dirt">Dirt</option><option value="gravel">Gravel</option>
              <option value="paved">Paved</option><option value="mixed">Mixed</option><option value="other">Other</option>
            </select></label>
            <label>Difficulty <select value={editingSeg.difficulty || ''} onChange={e => setEditingSeg({...editingSeg, difficulty: e.target.value || null})}>
              <option value="">—</option><option value="easy">Easy</option><option value="moderate">Moderate</option><option value="difficult">Difficult</option>
            </select></label>
            <label>Status <select value={editingSeg.maintenance_status || 'clear'} onChange={e => setEditingSeg({...editingSeg, maintenance_status: e.target.value})}>
              <option value="clear">Clear</option><option value="light_weeds">Light Weeds</option>
              <option value="heavy_weeds">Heavy Weeds</option><option value="other_maintenance">Other Maintenance</option>
              <option value="closed_impassable">Closed/Impassable</option>
            </select></label>
            <label>Footing Quality <select value={editingSeg.footing_quality ?? ''} onChange={e => setEditingSeg({...editingSeg, footing_quality: e.target.value === '' ? null : Number(e.target.value)})}>
              <option value="">Not rated</option>
              {FOOTING_SCORES.map(n => <option key={n} value={n}>{n} — {footingDescription(n)}</option>)}
            </select></label>
          </div>
          <div style={{marginTop: '0.75rem', display: 'flex', gap: '0.5rem'}}>
            <button className="btn-sm btn-primary" onClick={async () => {
              try {
                const { id, assigned_volunteer_id: ownerId, ...data } = editingSeg;
                await segmentsApi.update(id, data);
                const before = allSegments.find(s => s.id === id);
                if (canAssign && (before?.assigned_volunteer_id || null) !== (ownerId || null)) {
                  await segmentsApi.assignOwners([id], ownerId);
                }
                setMessage('Segment updated'); setEditingSeg(null); setSegFiles([]); loadSegments();
              } catch(err) { setMessage(`Error: ${err.message}`); }
            }}>Save</button>
            <button className="btn-sm btn-secondary" onClick={() => { setEditingSeg(null); setSegFiles([]); }}>Cancel</button>
            {canDeleteSeg && (
              <button className="btn-sm btn-danger" style={{marginLeft: 'auto'}} onClick={handleDeleteSeg}>Delete Segment</button>
            )}
          </div>

          {/* Agreement Documents */}
          <div style={{marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6'}}>
            <h4 style={{fontSize: '0.9rem', marginBottom: '0.5rem'}}>Agreement Documents ({segFiles.length})</h4>
            {segFiles.length > 0 && (
              <div style={{marginBottom: '0.5rem'}}>
                {segFiles.map(f => {
                  const localUrl = filesApi.downloadUrl(f.file_path);
                  return (
                  <div key={f.id} style={{display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.85rem'}}>
                    {localUrl ? (
                      <a href={localUrl} target="_blank" rel="noopener noreferrer" style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        📄 {f.file_name}
                      </a>
                    ) : (
                      <a href="#" style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={async (e) => {
                        e.preventDefault();
                        const url = await filesApi.getDownloadUrl(f.id);
                        window.open(url, '_blank');
                      }}>
                        📄 {f.file_name}
                      </a>
                    )}
                    <span style={{color: '#6c757d', fontSize: '0.75rem', whiteSpace: 'nowrap'}}>
                      {f.trail_name && !f.segment_id ? '(trail)' : f.segment_id ? '(segment)' : '(unlinked)'}
                    </span>
                    <button className="btn-sm btn-danger" style={{padding: '0.1rem 0.4rem', fontSize: '0.75rem'}} onClick={() => handleDeleteFile(f.id)}>✕</button>
                  </div>
                  );
                })}
              </div>
            )}
            <div style={{marginTop: '0.5rem'}}>
              <input
                type="text"
                placeholder="Search documents to link..."
                value={fileSearch}
                onChange={e => setFileSearch(e.target.value)}
                style={{width: '100%', padding: '0.4rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px'}}
              />
              {fileSearch.length >= 2 && (
                <div style={{maxHeight: '150px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '4px', marginTop: '0.25rem', background: '#fff'}}>
                  {allFiles
                    .filter(f => f.file_name.toLowerCase().includes(fileSearch.toLowerCase()))
                    .filter(f => !segFiles.some(sf => sf.file_path === f.file_path))
                    .slice(0, 20)
                    .map(f => (
                      <div key={f.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.3rem 0.5rem', fontSize: '0.8rem', borderBottom: '1px solid #f0f0f0', cursor: 'pointer'}}
                        onClick={() => handleLinkFile(f.id)}>
                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>📄 {f.file_name}</span>
                        <button className="btn-sm btn-primary" style={{padding: '0.1rem 0.4rem', fontSize: '0.7rem', marginLeft: '0.5rem', flexShrink: 0}}>Link</button>
                      </div>
                    ))}
                  {allFiles.filter(f => f.file_name.toLowerCase().includes(fileSearch.toLowerCase())).filter(f => !segFiles.some(sf => sf.file_path === f.file_path)).length === 0 && (
                    <div style={{padding: '0.3rem 0.5rem', fontSize: '0.8rem', color: '#6c757d'}}>No matching documents</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {canAssign && (() => {
        const owned = allSegments.filter(s => s.assigned_volunteer_id).length;
        const countFor = (id) => allSegments.filter(s => s.assigned_volunteer_id === id).length;
        const strays = [...new Map(allSegments
          .filter(s => s.assigned_volunteer_id && !roster.some(v => v.id === s.assigned_volunteer_id))
          .map(s => [s.assigned_volunteer_id, s.assigned_volunteer_name || 'Unknown'])).entries()];
        const trails = [...new Set(allSegments.map(s => s.trail_name))].sort();
        const bulkTargets = allSegments.filter(s => s.trail_name === bulk.trail
          && (!bulk.onlyUnassigned || !s.assigned_volunteer_id));
        const chip = (key, label, active) => (
          <button key={key} type="button" className={`owner-chip ${active ? 'active' : ''}`}
            onClick={() => setOwnerFilter(active ? '' : key)}>{label}</button>
        );
        return (
          <div className="owner-panel">
            <div className="owner-panel-head">
              <h3>Trail owners</h3>
              <span className="portal-muted">{owned} of {allSegments.length} segments have an owner · click a name to filter the table</span>
            </div>
            <div className="owner-chips">
              {chip('none', `No owner (${allSegments.length - owned})`, ownerFilter === 'none')}
              {roster.map(v => chip(v.id, `${v.display_name} (${countFor(v.id)})`, ownerFilter === v.id))}
              {strays.map(([id, name]) => chip(id, `⚠️ ${name} (${countFor(id)}) — not a volunteer`, ownerFilter === id))}
            </div>
            <div className="owner-bulk">
              <span>Assign a whole trail:</span>
              <select value={bulk.trail} onChange={e => setBulk({ ...bulk, trail: e.target.value })}>
                <option value="">Choose a trail…</option>
                {trails.map(t => {
                  const segs = allSegments.filter(s => s.trail_name === t);
                  const free = segs.filter(s => !s.assigned_volunteer_id).length;
                  return <option key={t} value={t}>{t} — {segs.length} segment{segs.length === 1 ? '' : 's'}{free ? `, ${free} without owner` : ''}</option>;
                })}
              </select>
              <span>to</span>
              <OwnerSelect value={bulk.owner} roster={roster} onChange={v => setBulk({ ...bulk, owner: v || '' })} />
              <label className="owner-bulk-only">
                <input type="checkbox" checked={bulk.onlyUnassigned} onChange={e => setBulk({ ...bulk, onlyUnassigned: e.target.checked })} />
                only segments without an owner
              </label>
              <button className="btn-sm btn-primary" disabled={assigning || !bulk.trail || !bulk.owner || bulkTargets.length === 0}
                onClick={() => assignOwners(bulkTargets.map(s => s.id), bulk.owner)}>
                {assigning ? 'Assigning…' : `Assign ${bulkTargets.length} segment${bulkTargets.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        );
      })()}

      <input
        type="text"
        placeholder="Search by trail name, APN, owner, address, email, phone..."
        value={segFilter}
        onChange={e => setSegFilter(e.target.value)}
        style={{ width: '100%', padding: '0.5rem', marginBottom: '0.75rem', fontSize: '0.95rem', border: '1px solid #ced4da', borderRadius: '4px' }}
      />

      <table className="admin-table"><thead><tr>
        <th>Trail</th><th>#</th><th>Category</th><th>Access Type</th><th>Owner</th><th>Footing</th><th>Audit</th><th>Geo</th><th>Actions</th>
      </tr></thead><tbody>
        {allSegments.filter(seg => {
          if (ownerFilter === 'none' && seg.assigned_volunteer_id) return false;
          if (ownerFilter && ownerFilter !== 'none' && seg.assigned_volunteer_id !== ownerFilter) return false;
          if (!segFilter) return true;
          const q = segFilter.toLowerCase();
          return [seg.trail_name, seg.segment_label, seg.apn, seg.address, seg.landowner_name, seg.contact_email, seg.contact_phone, seg.agreement_ref, seg.description, seg.category, seg.assigned_volunteer_name]
            .some(field => field && field.toLowerCase().includes(q));
        }).map((seg) => (
          <tr key={seg.id}>
            <td><Link to={`/?segment=${seg.id}`} style={{color: 'var(--green-dark)'}}>{seg.trail_name}</Link>
              {seg.segment_label && <div style={{fontSize: '0.8rem', color: '#6c757d'}}>{seg.segment_label}</div>}</td>
            <td>{seg.segment_number}</td>
            <td>
              <span className={`category-badge category-${seg.category || 'internal'}`}>{categoryLabel(seg.category)}</span>
              {seg.category === 'expansion' && (
                <div style={{fontSize: '0.75rem', color: '#6c757d'}}>{expansionStatusLabel(seg.expansion_status)}</div>
              )}
            </td>
            <td>
              {seg.access_type_name ? (
                <span style={{ borderLeft: `3px solid ${seg.access_type_color}`, paddingLeft: '0.4rem' }}>{seg.access_type_name}</span>
              ) : '—'}
            </td>
            <td style={{fontSize: '0.8rem'}}>
              {canAssign ? (
                <OwnerSelect value={seg.assigned_volunteer_id} fallbackName={seg.assigned_volunteer_name} roster={roster}
                  onChange={v => assignOwners([seg.id], v)} disabled={assigning}
                  style={{fontSize: '0.8rem', padding: '0.15rem', maxWidth: '11rem'}} />
              ) : (seg.assigned_volunteer_name || '—')}
            </td>
            <td style={{whiteSpace: 'nowrap'}} title={footingDescription(seg.footing_quality)}>
              {seg.footing_quality ? (
                <><span className="footing-dot" style={{backgroundColor: footingColor(seg.footing_quality), marginRight: '0.3rem'}} />{seg.footing_quality}</>
              ) : '—'}
            </td>
            <td style={{textAlign: 'center'}}>
              <input type="checkbox" checked={!!seg.audit_2026} onChange={async () => {
                try { await segmentsApi.toggleAudit(seg.id); loadSegments(); }
                catch (err) { setMessage(`Error: ${err.message}`); }
              }} />
            </td>
            <td>{seg.geometry ? '✅' : '❌'}</td>
            <td>
              <button className="btn-sm btn-secondary" onClick={() => startEditSeg(seg)}>Edit</button>
              <input type="file" accept=".kml" style={{width: '100px', fontSize: '0.7rem'}}
                onChange={(e) => { if (e.target.files[0]) handleKmlUpload(seg.id, e.target.files[0]); }} />
            </td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}
